import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useUser } from '@/allauth_auth/hooks.js';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

interface InternalGuardProps {
  children: ReactNode;
}

/**
 * Guards routes to only allow internal Ahoy users.
 * Uses the shared useIsInternalUser hook to avoid duplicate access-check fetches.
 */
export function InternalGuard({ children }: InternalGuardProps) {
  const user = useUser();
  const isInternal = useIsInternalUser();

  // Still loading user or internal status — show loading indicator.
  // useIsInternalUser returns:
  //   null  = still loading (no cache, revalidation in progress)
  //   true  = confirmed internal access
  //   false = confirmed non-internal
  if (!user?.id || isInternal === null) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <LoadingIndicator size='md' />
      </div>
    );
  }

  // User is confirmed non-internal — redirect
  if (!isInternal) {
    return <Navigate to='/organizations' replace />;
  }

  return children;
}
